import * as React from 'react'
import {
  graphChangeTokenMoved,
  invalidateGraphData,
  subscribeGraphChangeSignals,
  useActiveBranch,
  useAppDispatch,
  useGetGraphChangeTokenQuery,
  type GraphChangeObservation
} from '@axilon-apps/domains-ontology-ui-state'

import { useDocumentVisible } from '../../hooks/use-document-visible'

const RECOVERY_POLL_MS = 5_000
const CONNECTED_POLL_MS = 60_000

/** Global backstop for graph writes the current browser tab did not observe. */
export function GraphChangePoller() {
  const dispatch = useAppDispatch()
  const activeBranch = useActiveBranch()
  const branchKey = activeBranch ?? 'main'
  const visible = useDocumentVisible()
  const [connected, setConnected] = React.useState(false)
  const { currentData, refetch } = useGetGraphChangeTokenQuery(branchKey, {
    pollingInterval: connected ? CONNECTED_POLL_MS : RECOVERY_POLL_MS,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true
  })
  const previous = React.useRef<GraphChangeObservation | undefined>(undefined)
  const refetchToken = React.useRef(refetch)
  refetchToken.current = refetch

  React.useEffect(() => {
    setConnected(false)
    if (!visible) return

    const controller = new AbortController()
    void subscribeGraphChangeSignals({
      onSignal: () => {
        void refetchToken.current()
      },
      onStatus: (status) => setConnected(status === 'connected'),
      signal: controller.signal
    })
    return () => controller.abort()
  }, [branchKey, visible])

  React.useEffect(() => {
    if (!currentData?.token) return

    const next = { branchKey, token: currentData.token }
    const moved = graphChangeTokenMoved(previous.current, next)
    previous.current = next
    if (moved) invalidateGraphData(dispatch)
  }, [branchKey, currentData?.token, dispatch])

  return null
}
